import { useCallback, useState } from 'react';

import Config from '@config';
import { t } from '@i18n';
import api from '@services/api';
import pixel from '@services/pixel';
import { getPlanValue } from '@utils/money';
import { phoneToE164 } from '@utils/phone';
import { savePendingStripeCharge } from '@utils/stripeCheckout';

import {
  buildStripeIntentPayload,
  getStripeBillingDetails,
  validateAndBuildAsaasPayload,
  validateStripeCustomer,
} from '../lib/paymentValidation';
import type { CheckoutValuesRef } from '../types';

interface UseCheckoutPaymentsParams {
  valuesRef: CheckoutValuesRef;
  chargeIdRef: { current: any };
  plan: any;
  currency: string;
  buyerCountry: string;
  setPixInfo: (value: any) => void;
  showError: (message: string) => void;
  handlePaid: (redirect?: string | null) => void;
  watchPixPayment: (redirect: string | null) => void;
  waitForStripePayment: () => Promise<unknown>;
}

export function useCheckoutPayments({
  valuesRef,
  chargeIdRef,
  plan,
  currency,
  buyerCountry,
  setPixInfo,
  showError,
  handlePaid,
  watchPixPayment,
  waitForStripePayment,
}: UseCheckoutPaymentsParams) {
  const [isAsaasLoading, setIsAsaasLoading] = useState(false);

  const trackInitiatedCheckout = useCallback(() => {
    const values = valuesRef.current;
    const checkoutData = {
      email: values.email,
      name: values.name,
      phone: phoneToE164(values.phone),
      cpf: values.cpf,
      plan,
      value: getPlanValue(plan),
      currency,
      installments: values.installments || 1,
    };

    pixel.setUserData(checkoutData);
    pixel.dispatchEvent(pixel.events.initiate_checkout, checkoutData);
  }, [currency, plan, valuesRef]);

  const handleAsaasPayment = useCallback(async () => {
    const values = valuesRef.current;

    try {
      trackInitiatedCheckout();
      const requestPayload = validateAndBuildAsaasPayload(values);
      setIsAsaasLoading(true);

      const response = await api.post(Config.payEndpoint, requestPayload);

      if (!response.data?.success) {
        console.error('Payment failed:', response.data);
        throw new Error(t('payment.error.generic'));
      }

      const redirect = response.data.userCreated
        ? `${Config.authEndpoint}/request-reset-password?email=${values.email}&mode=post-checkout`
        : null;

      if (values.method === 'credit') {
        handlePaid(redirect);
      } else if (values.method === 'pix') {
        chargeIdRef.current = response.data.charge.id;
        setPixInfo({ ...response.data.pix });
        watchPixPayment(redirect);
      }
    } catch (error) {
      console.error(error);
      showError(error?.message || t('payment.error.generic'));
    } finally {
      setIsAsaasLoading(false);
    }
  }, [chargeIdRef, handlePaid, setPixInfo, showError, trackInitiatedCheckout, valuesRef, watchPixPayment]);

  const createStripeIntent = useCallback(async () => {
    const values = valuesRef.current;
    const requestPayload = buildStripeIntentPayload(values, buyerCountry);
    trackInitiatedCheckout();

    const response = await api.post('checkout/payment-intent', requestPayload);

    if (!response.data?.success || !response.data?.clientSecret) {
      console.error('Stripe payment intent failed:', response.data);
      throw new Error(t('payment.error.prepare'));
    }

    const chargeId = response.data.chargeId || response.data.charge?.id;
    if (!chargeId) throw new Error(t('payment.error.missingCharge'));

    chargeIdRef.current = chargeId;
    savePendingStripeCharge(chargeId);

    return {
      clientSecret: response.data.clientSecret,
      amount: response.data.amount || null,
      // Server-authoritative Stripe context: the render-time values came from
      // the product payload/config, but the intent response is the source of
      // truth (prod ships no hardcoded key).
      publishableKey: response.data.publishableKey || null,
      stripeAccount: response.data.stripeAccount || null,
      currency: response.data.currency || null,
    };
  }, [buyerCountry, chargeIdRef, trackInitiatedCheckout, valuesRef]);

  const preValidateStripe = useCallback(() => {
    validateStripeCustomer(valuesRef.current);
  }, [valuesRef]);

  const billingDetails = useCallback(() => {
    return getStripeBillingDetails(valuesRef.current);
  }, [valuesRef]);

  return {
    isAsaasLoading,
    handleAsaasPayment,
    preValidateStripe,
    createStripeIntent,
    getStripeBillingDetails: billingDetails,
    handleStripeConfirmed: waitForStripePayment,
    handleStripeError: showError,
  };
}

